import { useEffect, useState } from "react";
import {
  Modal, ModalDialog, ModalOverflow, ModalClose,
  Box, Button, Stack, Typography, Input, Switch,
  Sheet, Divider, Chip, FormControl, FormLabel,
  CircularProgress, Alert,
} from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";

import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";

import { usePermissions } from "../../usePermissions";
import { splitFullName } from "../../shared/nameUtils";
import { APICreateChildAccount, APIUpdateChildAccount } from "../../api_access";
import MaskedInput from "../JoyMaskedInput";


// ── Все типы прав с иконками и описаниями ──────────────────────────
const ALL_PERMISSIONS = [
  { type: "create_application",      label: "Создание заявок",            desc: "Может создавать новые заявки" },
  { type: "edit_application",        label: "Редактирование заявок",      desc: "Может редактировать заявки" },
  { type: "delete_application",      label: "Удаление заявок",            desc: "Может удалять заявки" },
  { type: "view_application",        label: "Просмотр заявок",            desc: "Видит заявки, чат по заявкам и получает уведомления по ним" },
  { type: "add_access",              label: "Добавление доступов",        desc: "Может добавлять сотрудников и получает уведомления о новых сотрудниках" },
  { type: "edit_access",             label: "Редактирование доступов",    desc: "Может редактировать сотрудников" },
  { type: "edit_access_permissions", label: "Редактирование прав",        desc: "Может менять права сотрудников" },
  { type: "delete_access",           label: "Удаление доступов",          desc: "Может удалять сотрудников" },
  { type: "view_payments",           label: "Просмотр платежей",          desc: "Видит раздел «Платежи» и историю оплат" },
  { type: "manage_payments",         label: "Управление платежами",       desc: "Может оплачивать и пересоздавать ссылки на оплату" },
];


export default function ChildAccountModal({ open, onClose, editAccount, onSuccess }) {
  const isEdit = !!editAccount;

  const [phone, setPhone] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [patronymic, setPatronymic] = useState("");
  const [email, setEmail] = useState("");
  const [autoAddToChats, setAutoAddToChats] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { hasPermission } = usePermissions();
  const canEditPermissions = hasPermission('edit_access_permissions');

  // ── Инициализация формы ──────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    if (isEdit) {
      setPhone(editAccount.phone || "");
      // Бэк может вернуть либо новые поля, либо только full_name
      const split = splitFullName(editAccount.full_name);
      setLastName(editAccount.last_name ?? split.last_name);
      setFirstName(editAccount.name ?? split.name);
      setPatronymic(editAccount.patronymic ?? split.patronymic);
      setEmail(editAccount.email || "");
      setAutoAddToChats(editAccount.auto_add_to_chats ?? true);
      // Преобразуем массив прав в объект { permission_type: is_active }
      const permsObj = {};
      ALL_PERMISSIONS.forEach(p => { permsObj[p.type] = false; });
      (editAccount.permissions || []).forEach(p => {
        permsObj[p.permission_type] = p.is_active;
      });
      setPermissions(permsObj);
    } else {
      setPhone("");
      setLastName("");
      setFirstName("");
      setPatronymic("");
      setEmail("");
      setAutoAddToChats(true);
      // По умолчанию все права включены
      const permsObj = {};
      ALL_PERMISSIONS.forEach(p => { permsObj[p.type] = true; });
      setPermissions(permsObj);
    }
    setError("");
  }, [open, editAccount]);

  // ── Переключение права ───────────────────────────────────────────
  const togglePermission = (type) => {
    setPermissions(prev => ({ ...prev, [type]: !prev[type] }));
  };

  // ── Сохранение ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (!phone.trim()) {
      setError("Укажите номер телефона");
      return;
    }

    setSaving(true);
    setError("");

    const permsArray = ALL_PERMISSIONS.map(p => ({
      permission_type: p.type,
      is_active: !!permissions[p.type],
    }));

    const payload = {
      phone: phone.trim(),
      last_name:  lastName.trim(),
      name:       firstName.trim(),
      patronymic: patronymic.trim(),
      email: email.trim() || null,
      auto_add_to_chats: autoAddToChats,
      permissions: permsArray,
    };

    let result;
    if (isEdit) {
      result = await APIUpdateChildAccount(editAccount.id, payload);
    } else {
      result = await APICreateChildAccount(payload);
    }

    setSaving(false);

    if (result?.status === "error") {
      setError(result.detail || result.message || "Произошла ошибка");
      return;
    }

    onSuccess?.();
    onClose();
  };

  // ── Включить/выключить все ───────────────────────────────────────
  const allEnabled = ALL_PERMISSIONS.every(p => permissions[p.type]);
  const toggleAll = () => {
    const newVal = !allEnabled;
    const permsObj = {};
    ALL_PERMISSIONS.forEach(p => { permsObj[p.type] = newVal; });
    setPermissions(permsObj);
  };


  return (
    <AnimatePresence>
      {open && (
        <Modal
          open={open}
          onClose={onClose}
          sx={{ zIndex: 10012 }}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0, backdropFilter: "blur(0px)" },
              animate: { opacity: 1, backdropFilter: "blur(8px)" },
              exit:    { opacity: 0, backdropFilter: "blur(0px)" },
              transition: { duration: 0.2 },
            },
          }}
        >
          <ModalOverflow sx={{ p: 0 }}>
            <ModalDialog
              size="lg"
              component={motion.div}
              initial={{ opacity: 0, y: -16, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.97 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              sx={(theme) => ({
                p: 0,
                overflow: "hidden",
                borderRadius: "24px",
                my: "20px !important",
                [theme.breakpoints.up("md")]: { width: 560 },
                [theme.breakpoints.down("md")]: { width: "95vw" },
                [theme.breakpoints.down("sm")]: {
                  width: "100vw", borderRadius: 0,
                  m: "0px !important", minWidth: "100dvw",
                  minHeight: "100dvh",
                },
              })}
            >
              {/* ─── Шапка ─── */}
              <Box
                sx={{
                  px: 3, py: 2.5,
                  background: isEdit
                    ? "linear-gradient(135deg, #f59e0b22, #f59e0b08)"
                    : "linear-gradient(135deg, #6366f122, #8b5cf608)",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Sheet
                      variant="soft"
                      color={isEdit ? "warning" : "primary"}
                      sx={{
                        width: 44, height: 44, borderRadius: "14px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      {isEdit ? <EditRoundedIcon /> : <PersonAddRoundedIcon />}
                    </Sheet>
                    <Box>
                      <Typography level="title-lg" fontWeight={800}>
                        {isEdit ? "Редактирование" : "Новый сотрудник"}
                      </Typography>
                      <Typography level="body-xs" color="neutral">
                        {isEdit ? "Измените данные и права доступа" : "Добавьте сотрудника к вашему кабинету"}
                      </Typography>
                    </Box>
                  </Stack>
                  <ModalClose
                    variant="soft" color="neutral"
                    sx={{ position: "static", borderRadius: "50%" }}
                  />
                </Stack>
              </Box>

              {/* ─── Тело ─── */}
              <Box sx={{ px: 3, py: 2.5, overflowY: "auto", maxHeight: "70vh" }}>

                {error && (
                  <Alert
                    color="danger" variant="soft"
                    startDecorator={<WarningRoundedIcon />}
                    sx={{ mb: 2, borderRadius: "14px" }}
                  >
                    {error}
                  </Alert>
                )}

                {/* ─── Данные аккаунта ─── */}
                <Typography
                  level="body-xs" fontWeight={800}
                  sx={{
                    textTransform: "uppercase", letterSpacing: "0.08em",
                    color: "primary.500", mb: 1.5,
                  }}
                >
                  Данные аккаунта
                </Typography>

                <Stack spacing={2} sx={{ mb: 3 }}>
                  <FormControl>
                    <FormLabel>Телефон *</FormLabel>
                    <MaskedInput
                      mask="+7 (000) 000-00-00"
                      placeholder="+7 (000) 000-00-00"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      size="lg"
                      sx={{ fontSize: "1.05rem", fontWeight: 600, letterSpacing: "0.03em" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Фамилия</FormLabel>
                    <Input
                      placeholder="Иванов"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      sx={{ borderRadius: "12px" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Имя</FormLabel>
                    <Input
                      placeholder="Иван"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      sx={{ borderRadius: "12px" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>
                      Отчество <Typography component="span" level="body-xs" color="neutral">(необязательно)</Typography>
                    </FormLabel>
                    <Input
                      placeholder="Иванович"
                      value={patronymic}
                      onChange={(e) => setPatronymic(e.target.value)}
                      sx={{ borderRadius: "12px" }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      placeholder="email@company.ru"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      sx={{ borderRadius: "12px" }}
                    />
                  </FormControl>

                  <Sheet
                    variant="soft" color="neutral"
                    sx={{ borderRadius: "12px", p: 1.5, display: "flex",
                          alignItems: "center", justifyContent: "space-between", gap: 1.5 }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography level="body-sm" fontWeight={700}>
                        Авто-добавление в чаты заявок
                      </Typography>
                      <Typography level="body-xs" color="neutral">
                        Добавлять сотрудника в чат автоматически при создании заявки
                      </Typography>
                    </Box>
                    <Switch
                      checked={autoAddToChats}
                      onChange={(e) => setAutoAddToChats(e.target.checked)}
                    />
                  </Sheet>
                </Stack>
                
                {canEditPermissions && (
                  <>
                    <Divider sx={{ my: 2 }} />

                    {/* ─── Права доступа ─── */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <SecurityRoundedIcon sx={{ fontSize: 18, color: "primary.500" }} />
                        <Typography
                          level="body-xs" fontWeight={800}
                          sx={{
                            textTransform: "uppercase", letterSpacing: "0.08em",
                            color: "primary.500",
                          }}
                        >
                          Права доступа
                        </Typography>
                      </Stack>
                      <Button
                        size="sm" variant="soft" color="neutral"
                        onClick={toggleAll}
                        sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 12 }}
                      >
                        {allEnabled ? "Снять все" : "Включить все"}
                      </Button>
                    </Stack>

                    <Stack spacing={1}>
                      {ALL_PERMISSIONS.map((perm, i) => (
                        <motion.div
                          key={perm.type}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.03 }}
                        >
                          <Sheet
                            variant="outlined"
                            sx={{
                              borderRadius: "14px",
                              p: 1.5,
                              px: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              transition: "all 0.2s",
                              borderColor: permissions[perm.type] ? "primary.300" : "neutral.200",
                              bgcolor: permissions[perm.type] ? "primary.50" : "transparent",
                              cursor: "pointer",
                              "&:hover": {
                                borderColor: "primary.400",
                                transform: "translateX(2px)",
                              },
                            }}
                            onClick={() => togglePermission(perm.type)}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                {permissions[perm.type] ? (
                                  <CheckCircleRoundedIcon sx={{ fontSize: 16, color: "primary.500" }} />
                                ) : (
                                  <CloseRoundedIcon sx={{ fontSize: 16, color: "neutral.400" }} />
                                )}
                                <Typography level="body-sm" fontWeight={600}>
                                  {perm.label}
                                </Typography>
                              </Stack>
                              <Typography level="body-xs" color="neutral" sx={{ ml: 3.5, mt: 0.2 }}>
                                {perm.desc}
                              </Typography>
                            </Box>

                            <Switch
                              size="sm"
                              checked={!!permissions[perm.type]}
                              onChange={() => togglePermission(perm.type)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ ml: 1.5 }}
                            />
                          </Sheet>
                        </motion.div>
                      ))}
                    </Stack>
                  </>
                )}
              </Box>

              {/* ─── Подвал ─── */}
              <Box
                sx={{
                  px: 3, py: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.level1",
                }}
              >
                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Button
                    variant="soft" color="neutral"
                    onClick={onClose}
                    sx={{ borderRadius: "12px", fontWeight: 600 }}
                  >
                    Отмена
                  </Button>
                  <Button
                    loading={saving}
                    onClick={handleSave}
                    sx={{
                      borderRadius: "12px", fontWeight: 700,
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                      "&:hover": { boxShadow: "0 6px 24px rgba(99,102,241,0.45)" },
                    }}
                  >
                    {isEdit ? "Сохранить" : "Создать"}
                  </Button>
                </Stack>
              </Box>

            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>
  );
}